import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { articles } from '@/mocks/jiese';
import SEOHead from '@/components/feature/SEOHead';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>文章未找到</h1>
          <p className="text-sm text-foreground-600 mb-4">请检查文章链接是否正确</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const navigateTo = (path: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 戒色完全指南`}
        description={article.excerpt}
        keywords={`戒色,${article.tags.join(',')},${article.categoryName},戒色方法,戒色心得`}
        canonicalUrl={`https://purifypath.github.io/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        breadcrumbList={[
          { name: '首页', url: 'https://purifypath.github.io/' },
          { name: article.categoryName, url: `https://purifypath.github.io/category/${article.category}` },
          { name: article.title, url: `https://purifypath.github.io/article/${article.slug}` },
        ]}
        articleData={{
          headline: article.title,
          datePublished: article.date,
          dateModified: article.date,
          authorName: article.author,
          image: article.image,
        }}
      />

      <main className="pt-16">
        {/* Article Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5 flex-wrap" aria-label="Breadcrumb">
              <button onClick={() => navigateTo('/')} className="hover:text-primary-600 transition-colors cursor-pointer">首页</button>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <button onClick={() => navigateTo(`/category/${article.category}`)} className="hover:text-primary-600 transition-colors cursor-pointer">{article.categoryName}</button>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <span className="inline-block px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
              {article.categoryName}
            </span>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1"><i className="ri-user-line"></i> {article.author}</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.date}</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <div className="w-full max-w-[800px] mx-auto px-4 md:px-6 -mt-6">
          <div className="w-full h-[350px] md:h-[420px] rounded-lg overflow-hidden">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
          </div>
        </div>

        {/* Article Content */}
        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                相关文章推荐
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <article
                    key={ra.id}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    onClick={() => navigateTo(`/article/${ra.slug}`)}
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-600 mb-3 line-clamp-2 leading-relaxed">{ra.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{ra.date}</span>
                        <span>{ra.readTime}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}